import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Path, Stack, ArrowsClockwise, Lightning, MapTrifold, SortAscending } from '@phosphor-icons/react';
import { getPickingSettings, updatePickingSettings, recomputeAbc } from '../lib/api';

const STRATEGIES = [
  {
    value: 'location_numeric',
    label: 'Location numeric',
    desc: 'Walk by aisle-bay-shelf (default · nearest-neighbour)',
    Icon: MapTrifold,
  },
  {
    value: 'sku_numeric',
    label: 'SKU numeric',
    desc: 'Sort by SKU code · location as tie-breaker',
    Icon: SortAscending,
  },
  {
    value: 'abc_velocity',
    label: 'ABC velocity',
    desc: 'A-class first (most-picked), then B, then C · location within class',
    Icon: Lightning,
  },
];

export default function PickStrategyCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [windowDays, setWindowDays] = useState(90);

  const load = async () => {
    try { const r = await getPickingSettings(); setS(r.data); setWindowDays(r.data?.abc_window_days || 90); }
    catch (e) { /* non-admin → silently hide */ }
  };
  useEffect(() => { load(); }, []);

  if (!s) return null;

  const save = async (strategy) => {
    setBusy(true);
    try {
      const r = await updatePickingSettings({ sort_strategy: strategy, abc_window_days: windowDays });
      setS(r.data);
      toast.success(`Pick strategy: ${strategy.replace('_', ' ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      const r = await recomputeAbc(windowDays);
      const d = r.data || {};
      toast.success(`ABC recomputed · A:${d.A} B:${d.B} C:${d.C} unclassified:${d.U}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recompute failed');
    } finally { setRecomputing(false); }
  };

  const dist = s.abc_distribution || { A: 0, B: 0, C: 0, U: 0, total: 0 };
  return (
    <div className="border border-border p-4 mb-4" data-testid="pick-strategy-card">
      <div className="flex items-center gap-2 mb-1">
        <Path size={18} weight="duotone" className="text-brand" />
        <div className="font-semibold tracking-tight">Pick path strategy</div>
        <Badge variant="outline" className="ml-auto" data-testid="pick-strategy-current">{s.sort_strategy.replace('_', ' ')}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Controls how SKUs are ordered inside every new pick list. Existing pick lists keep their original order.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
        {STRATEGIES.map(({ value, label, desc, Icon }) => {
          const active = s.sort_strategy === value;
          return (
            <button
              key={value}
              data-testid={`pick-strategy-${value}`}
              disabled={busy}
              onClick={() => save(value)}
              className={`text-left border p-3 transition-all ${active ? 'border-brand bg-brand/5' : 'border-border hover:border-foreground/40'}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} weight={active ? 'fill' : 'duotone'} className={active ? 'text-brand' : 'text-muted-foreground'} />
                <div className="font-bold text-sm">{label}</div>
                {active && <Badge className="ml-auto bg-brand">active</Badge>}
              </div>
              <div className="text-xs text-muted-foreground">{desc}</div>
            </button>
          );
        })}
      </div>

      <div className="border-t pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">ABC classification</div>
          <div className="text-xs text-muted-foreground" data-testid="pick-strategy-abc-last">
            {s.abc_last_computed_at ? `Last run ${new Date(s.abc_last_computed_at).toLocaleString()}` : 'Never run'}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Window (days)</Label>
            <Input type="number" data-testid="pick-strategy-window" className="w-24" value={windowDays} onChange={(e) => setWindowDays(parseInt(e.target.value) || 90)} />
          </div>
          <Button size="sm" data-testid="pick-strategy-recompute" onClick={recompute} disabled={recomputing}>
            <ArrowsClockwise size={14} className={`mr-1 ${recomputing ? 'animate-spin' : ''}`} /> {recomputing ? 'Recomputing…' : 'Recompute ABC'}
          </Button>
          <div className="ml-auto flex flex-wrap items-center text-xs" data-testid="pick-strategy-distribution">
            <Stack size={14} className="text-muted-foreground mr-2" />
            <Badge className="bg-emerald-600 mr-2">A · {dist.A}</Badge>
            <Badge className="bg-amber-500 mr-2">B · {dist.B}</Badge>
            <Badge className="bg-rose-500 mr-2">C · {dist.C}</Badge>
            <Badge variant="outline">unclassified · {dist.U}</Badge>
          </div>
        </div>
        {s.sort_strategy === 'abc_velocity' && dist.total === 0 && (
          <div className="mt-2 text-xs border border-amber-300 bg-amber-50 text-amber-900 p-2" data-testid="pick-strategy-warning">
            ABC classes haven't been computed yet — your picks will currently fall back to location order. Click "Recompute ABC" to label your SKUs.
          </div>
        )}
      </div>
    </div>
  );
}
